import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

// Theme definitions (extracted from compiled code)
const themes = {
  default: {
    id: 'default',
    name: 'Default Light',
    description: 'Clean and professional light theme',
    colors: {
      bgPrimary: '#f1f5f9',
      bgSecondary: '#ffffff',
      bgTertiary: '#f8fafc',
      bgAccent: '#2563eb',
      bgDanger: '#dc2626',
      bgSuccess: '#16a34a',
      bgWarning: '#d97706',
      textPrimary: '#1e293b',
      textSecondary: '#64748b',
      textTertiary: '#94a3b8',
      textInverse: '#ffffff',
      textAccent: '#2563eb',
      textDanger: '#dc2626',
      textSuccess: '#16a34a',
      textWarning: '#d97706',
      borderPrimary: '#e2e8f0',
      borderSecondary: '#cbd5e1',
      borderAccent: '#2563eb',
      borderFocus: '#3b82f6',
      surfacePrimary: '#ffffff',
      surfaceSecondary: '#f8fafc',
      surfaceHover: '#f1f5f9',
      surfaceActive: '#e2e8f0',
      buttonPrimary: '#2563eb',
      buttonPrimaryHover: '#1d4ed8',
      buttonSecondary: '#e2e8f0',
      buttonSecondaryHover: '#cbd5e1',
      buttonDanger: '#dc2626',
      buttonDangerHover: '#b91c1c',
      buttonSuccess: '#16a34a',
      buttonSuccessHover: '#15803d'
    }
  },
  'dark-slate': {
    id: 'dark-slate',
    name: 'Dark Slate',
    description: 'Slick & Sexy sci-fi theme with glassmorphism and cyan accents',
    colors: {
      bgPrimary: '#0f172a',
      bgSecondary: '#1e293b',
      bgTertiary: '#334155',
      bgAccent: '#06b6d4',
      bgDanger: '#ef4444',
      bgSuccess: '#10b981',
      bgWarning: '#f59e0b',
      textPrimary: '#f1f5f9',
      textSecondary: '#cbd5e1',
      textTertiary: '#94a3b8',
      textInverse: '#0f172a',
      textAccent: '#22d3ee',
      textDanger: '#f87171',
      textSuccess: '#34d399',
      textWarning: '#fbbf24',
      borderPrimary: '#334155',
      borderSecondary: '#475569',
      borderAccent: '#06b6d4',
      borderFocus: '#22d3ee',
      surfacePrimary: 'rgba(30, 41, 59, 0.7)',
      surfaceSecondary: 'rgba(51, 65, 85, 0.7)',
      surfaceHover: 'rgba(71, 85, 105, 0.7)',
      surfaceActive: 'rgba(100, 116, 139, 0.7)',
      buttonPrimary: '#06b6d4',
      buttonPrimaryHover: '#0891b2',
      buttonSecondary: '#334155',
      buttonSecondaryHover: '#475569',
      buttonDanger: '#ef4444',
      buttonDangerHover: '#dc2626',
      buttonSuccess: '#10b981',
      buttonSuccessHover: '#059669'
    }
  }
}

export const useThemeStore = defineStore('theme', () => {
  // State
  const currentThemeId = ref(localStorage.getItem('theme') || 'default')

  // Getters
  const currentTheme = computed(() => themes[currentThemeId.value] || themes.default)
  const availableThemes = computed(() => Object.values(themes))

  // Actions
  function setTheme(themeId) {
    if (themes[themeId]) {
      currentThemeId.value = themeId
      localStorage.setItem('theme', themeId)
      applyTheme()
    }
  }

  function applyTheme() {
    const theme = currentTheme.value
    const root = document.documentElement

    // Apply CSS custom properties
    Object.entries(theme.colors).forEach(([key, value]) => {
      // Convert camelCase to kebab-case for CSS variables
      const cssVar = '--' + key.replace(/([A-Z])/g, '-$1').toLowerCase()
      root.style.setProperty(cssVar, value)
    })

    console.log('[Theme] Applied theme:', theme.name)
  }

  return {
    currentThemeId,
    currentTheme,
    availableThemes,
    setTheme,
    applyTheme
  }
})
